import * as React from 'react';
import Fragment from 'react-dot-fragment';
import { observer } from 'mobx-react';
import { style } from 'typestyle';
import { colorBrand, colorGreyAccent, colorLightBlueGrey, colorWhite } from '../../theme/color';
import { ScrollableOverlay } from '../scrollable-overlay/scrollable-overlay';
import { ScrollableOverlayHeaderStyle } from '../../styles/scrollable-overlay';
import { Logo } from '../logo';
import { Loading } from '../loading/loading';
import PrismicDOM from 'prismic-dom';
import { IObservableArray, isObservableArray } from 'mobx';
import Tabs from '../tabs/tabs';
import { px } from 'csx';
import { abbreviateAmount } from '../../lib/helpers';
import ModalScreen from '../modal-screen';
import { RatecardSchema } from '../../types/schema';
import { Component, deps, inject } from '../../lib/component';

export interface RightsholderModalProps {
  show: boolean;
  title: string;
  ratecard: RatecardSchema;
  eulaContent: Error | IObservableArray<any> | null; // Using any instead of unknown given old TS version
  onRequestClose(): void;
  coupon?: any;
  i18n?: {
    title: string;
    strap: any;
    loading: string;
    not_available: string;
    close: string;
    first_discount: string;
    affiliate_discount: string;
  };
}

const styles = {
  body: style({
    backgroundColor: colorLightBlueGrey.toHexString(),
  }),
  header: style({
    height: px(64),
  }),
  heading: style({
    textAlign: 'center',
    fontSize: px(16),
    lineHeight: px(64),
    margin: 0,
  }),
  subheading: style({
    textAlign: 'center',
  }),
  steps: style({
    paddingLeft: px(20),
    $nest: {
      '& > li': {
        padding: px(16),
        paddingLeft: px(8),
        marginLeft: px(35),
        position: 'relative',
        $nest: {
          '&::after': {
            width: 'calc(100% + 55px)',
            height: px(1),
            content: '""',
            background: colorGreyAccent.toHexString(),
            display: 'block',
            marginLeft: px(-71),
            position: 'absolute',
            top: 0,
          },
          '&:first-child::after': {
            content: 'none',
          },
        },
      },
    },
  }),
  bullets: style({
    paddingLeft: px(42),
    margin: px(16),
    $nest: { '& > li': { paddingLeft: px(8) } },
  }),
};

@observer
@inject(deps) // TODO: Remove this when we work out the correct data for Rightsholder modals
export class RightsholderModal extends Component<RightsholderModalProps> {
  render() {
    const { show, title, onRequestClose, ratecard } = this.props;

    const viralViews = ratecard ? (ratecard.viewership * 3 > 500000 ? ratecard.viewership * 3 : 500000) : 500000;

    // TODO: Remove this when we work out the correct data for Rightsholder modals
    const tracks = this.props.model.basket.tracks.filter((track) => track.reporting_party.slug === 'umg');

    return (
      <ScrollableOverlay show={show} onRequestClose={onRequestClose}>
        {show && (
          <span className={styles.body}>
            <header className={styles.header}>
              <h3 className={styles.heading}>{title}</h3>
            </header>
            <main className={styles.body}>
              <Tabs>
                <Tabs.Tab heading="1">
                  <p>
                    Universal Music Group are involved in releasing the claim for tracks in your basket (listed below);
                    the claims will be sorted within 4 hours on a working day and within 48 on a non-working day.
                  </p>
                  <h5>Tracks</h5>
                  <ul>
                    {tracks.map((track, index) => {
                      const artists = track.artists
                        .map(function (artist) {
                          return artist.name;
                        })
                        .join(', ');
                      return (
                        <li key={index}>
                          {track.title} - {artists}
                        </li>
                      );
                    })}
                  </ul>
                </Tabs.Tab>
              </Tabs>
              <div>
                <h4 className={styles.subheading}>What do I need to do?</h4>
                <ol className={styles.steps}>
                  <li>Set each video’s privacy to ‘Unlisted’ until the claims are cleared.</li>
                  <li>
                    Email each video’s link and Lickd License ID to{' '}
                    <a href="mailto:support@lickd.co">support@lickd.co</a>.
                  </li>
                  <li>
                    Make sure you’re clued up on the license terms below – you’ll need to accept before you can continue
                    the checkout process.
                  </li>
                </ol>
                <ul className={styles.bullets}>
                  <li>
                    You can only use this track in a video on a YouTube channel you’ve registered with Lickd, subject to
                    the terms and restrictions set out in the license agreement.
                  </li>
                  <li>This track can’t be used in any ad, sponsored video, branded content or endorsement.</li>
                  <li>This track can’t be used for a song or lyric video.</li>
                  <li>You must include the correct attribution for this track in your video’s description.</li>
                  <li>If you breach the license agreement your content could be taken down.</li>
                  <li>
                    If your video goes viral (video views total of over {abbreviateAmount(viralViews)}) in the first 30
                    days of your content going public, your video could be reclaimed by the rightsholder.
                  </li>
                </ul>
              </div>
            </main>
          </span>
        )}
      </ScrollableOverlay>
    );
  }
}
